import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { LegalDocumentView } from "@/components/legal";
import { API_URL } from "@/lib/config";

export const Route = createFileRoute("/terms-condition")({
  head: () => ({
    meta: [
      { title: "Terms and Conditions | BuildWithJIT" },
      {
        name: "description",
        content:
          "Review the terms of service and conditions for using BuildWithJIT, reading our technical articles, and engaging with our platform.",
      },
      {
        name: "robots",
        content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
      },
      {
        name: "googlebot",
        content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
      },
      { property: "og:site_name", content: "BuildWithJIT" },
      { property: "og:title", content: "Terms and Conditions | BuildWithJIT" },
      {
        property: "og:description",
        content:
          "Review the terms of service and conditions for using BuildWithJIT, reading our technical articles, and engaging with our platform.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://buildwithjit.com/terms-condition" },
      { property: "og:image", content: "https://buildwithjit.com/og-share.png" },
      { property: "og:image:secure_url", content: "https://buildwithjit.com/og-share.png" },
      { property: "og:image:type", content: "image/png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "Terms and Conditions | BuildWithJIT" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@buildwithjit" },
      { name: "twitter:creator", content: "@buildwithjit" },
      { name: "twitter:title", content: "Terms and Conditions | BuildWithJIT" },
      {
        name: "twitter:description",
        content:
          "Review the terms of service and conditions for using BuildWithJIT, reading our technical articles, and engaging with our platform.",
      },
      { name: "twitter:image", content: "https://buildwithjit.com/og-share.png" },
    ],
    links: [
      { rel: "canonical", href: "https://buildwithjit.com/terms-condition" },
      { rel: "icon", href: "/favicon.ico", sizes: "any" },
      { rel: "icon", type: "image/png", sizes: "48x48", href: "/icon-48.png" },
      { rel: "icon", type: "image/png", sizes: "96x96", href: "/icon-96.png" },
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/icon-192.png" },
      { rel: "icon", type: "image/png", sizes: "512x512", href: "/icon-512.png" },
      { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
      { rel: "shortcut icon", href: "/favicon.ico" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
    ],
  }),
  loader: async () => {
    try {
      const res = await fetch(`${API_URL}/api/legal/terms`);
      if (res.ok) {
        const data = await res.json();
        return { terms: data.content || "" };
      }
    } catch (e) {
      console.warn(`Could not fetch terms from ${API_URL}:`, e);
    }
    return { terms: "" };
  },
  component: TermsConditionPage,
});

function TermsConditionPage() {
  const { terms: initialTerms } = Route.useLoaderData();
  const [terms, setTerms] = useState<string>(initialTerms || "");

  // Client-side hydration: sync live from database
  useEffect(() => {
    let active = true;
    fetch(`${API_URL}/api/legal/terms?_t=${Date.now()}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (active && data?.content) {
          setTerms(data.content);
        }
      })
      .catch((err) => console.warn("Could not sync terms from DB:", err));

    return () => {
      active = false;
    };
  }, []);

  return <LegalDocumentView title="Terms and Conditions" content={terms} />;
}
